import { useState } from "react";
import { toast } from "sonner";
import { Bluetooth, Printer, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui-bits";
import { usePrinter } from "@/context/PrinterContext";
import Barcode from "@/components/Barcode";
import IntakeLabelDialog from "@/components/intake/IntakeLabelDialog";

function PrintSheet({ intakes }) {
  return (
    <div className="print-area hidden print:block" data-testid="stocktake-print-sheet">
      {intakes.map((i) => (
        <div key={i.id} className="p-4 bg-white text-black" style={{ breakAfter: "page", pageBreakAfter: "always" }}>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold">Oz Bakery · Raw material</div>
          <div className="text-xl font-bold">{i.material_name}</div>
          <div className="text-sm font-mono">{i.internal_batch_code} · Supplier batch {i.supplier_batch}</div>
          <div className="text-sm">{i.supplier_name} · {i.quantity} {i.unit} · Best before {i.expiry_date?.slice(0, 10)}</div>
          {i.allergens?.length > 0 && <div className="text-sm font-bold">Contains: {i.allergens.join(", ")}</div>}
          <Barcode value={i.internal_batch_code} />
        </div>
      ))}
    </div>
  );
}

/** New intakes from an applied stock take, with bulk label printing. */
export default function StockTakeLabels({ result, onDone }) {
  const printer = usePrinter();
  const btReady = printer?.isConnected;
  const [progress, setProgress] = useState(null);
  const [labelFor, setLabelFor] = useState(null);
  const intakes = result.intakes || [];

  const printAll = async () => {
    if (!btReady) return window.print();
    for (let n = 0; n < intakes.length; n += 1) {
      setProgress(n + 1);
      const ok = await printer.printIntakeLabel(intakes[n]);
      if (ok === false) {
        setProgress(null);
        return toast.error(`Printing stopped at label ${n + 1}`);
      }
    }
    setProgress(null);
    toast.success(`Printed ${intakes.length} labels`);
  };

  return (
    <div className="space-y-6" data-testid="stocktake-applied">
      <div className="panel p-5 flex flex-col sm:flex-row sm:items-center gap-4 border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30">
        <CheckCircle2 className="h-10 w-10 text-emerald-600 shrink-0" />
        <div className="flex-1">
          <div className="font-display text-xl font-bold" data-testid="stocktake-applied-title">Stock switched over</div>
          <div className="text-sm text-stone-600 dark:text-stone-300" data-testid="stocktake-applied-summary">
            {result.wiped ?? 0} old intake rows set to 0 · {intakes.length} new intakes created
          </div>
        </div>
        <Button className="h-12 px-5 gap-2" onClick={printAll} disabled={!!progress || !intakes.length} data-testid="stocktake-print-all">
          {btReady ? <Bluetooth className="h-4 w-4" /> : <Printer className="h-4 w-4" />}
          {progress ? `Printing ${progress}/${intakes.length}…` : `Print all ${intakes.length} new labels`}
        </Button>
        {onDone && <Button variant="outline" className="h-12" onClick={onDone} data-testid="stocktake-done">Done</Button>}
      </div>
      <Panel title={`New intake labels (${intakes.length})`}>
        <div className="divide-y divide-border" data-testid="stocktake-new-intakes">
          {intakes.map((i) => (
            <div key={i.id} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`stocktake-new-${i.id}`}>
              <div className="font-mono font-semibold w-44 shrink-0">{i.internal_batch_code}</div>
              <div className="flex-1 min-w-0 truncate">{i.material_name} · {i.supplier_batch}</div>
              <div className="tnum">{+Number(i.quantity).toFixed(2)} {i.unit}</div>
              <Button variant="outline" size="sm" className="h-10" onClick={() => setLabelFor(i)} data-testid={`stocktake-print-${i.id}`}>
                <Printer className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </Panel>
      {!labelFor && <PrintSheet intakes={intakes} />}
      <IntakeLabelDialog labelFor={labelFor} onClose={() => setLabelFor(null)} btReady={btReady} printer={printer} />
    </div>
  );
}
